import { useCallback } from "react";
import { Room } from "livekit-client";
import {
  Share2,
  Mic,
  MicOff,
  Video,
  VideoOff,
  Monitor,
  MessageSquare,
  Settings,
  LogOut,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface BottomControlBarProps {
  room: Room;
  onToggleChat: () => void;
  onToggleControlPanel: () => void;
  onLeave: () => void;
}

export function BottomControlBar({
  room,
  onToggleChat,
  onToggleControlPanel,
  onLeave,
}: BottomControlBarProps) {
  const localParticipant = room.localParticipant;

  const handleToggleMic = useCallback(async () => {
    try {
      const enabled = !localParticipant.isMicrophoneEnabled;
      await localParticipant.setMicrophoneEnabled(enabled);
    } catch (error) {
      console.error("Error toggling microphone:", error);
    }
  }, [localParticipant]);

  const handleToggleCamera = useCallback(async () => {
    try {
      const enabled = !localParticipant.isCameraEnabled;
      await localParticipant.setCameraEnabled(enabled);
    } catch (error) {
      console.error("Error toggling camera:", error);
    }
  }, [localParticipant]);

  const handleShareScreen = useCallback(async () => {
    try {
      if (localParticipant.isScreenShareEnabled) {
        await localParticipant.setScreenShareEnabled(false);
      } else {
        await localParticipant.setScreenShareEnabled(true);
      }
    } catch (error) {
      console.error("Error toggling screen share:", error);
    }
  }, [localParticipant]);

  return (
    <div className="flex items-center justify-between gap-2  bg-[#0f0f0f] px-4 py-3">
      <Button variant="livekit" onClick={() => {}} aria-label="Chia sẻ">
        <Share2 className="size-5" />
        <span className="ml-2 hidden sm:inline">Chia sẻ</span>
      </Button>
      <div className="flex items-center gap-2">
        <div className="relative">
          <Button
            variant="livekit"
            onClick={handleToggleMic}
            aria-label={
              localParticipant.isMicrophoneEnabled ? "Tắt mic" : "Bật mic"
            }
            className={cn(
              !localParticipant.isMicrophoneEnabled && "bg-destructive/20"
            )}
          >
            {localParticipant.isMicrophoneEnabled ? (
              <Mic className="size-5" />
            ) : (
              <MicOff className="size-5" />
            )}
            <span className="ml-2 hidden sm:inline">Microphone</span>
          </Button>
        </div>

        <div className="relative">
          <Button
            variant="livekit"
            onClick={handleToggleCamera}
            aria-label={
              localParticipant.isCameraEnabled ? "Tắt camera" : "Bật camera"
            }
            className={cn(
              !localParticipant.isCameraEnabled && "bg-destructive/20"
            )}
          >
            {localParticipant.isCameraEnabled ? (
              <Video className="size-5" />
            ) : (
              <VideoOff className="size-5" />
            )}
            <span className="ml-2 hidden sm:inline">Camera</span>
          </Button>
        </div>

        <Button
          variant="livekit"
          onClick={handleShareScreen}
          aria-label="Chia sẻ màn hình"
          className={cn(
            localParticipant.isScreenShareEnabled && "bg-primary/20"
          )}
        >
          <Monitor className="size-5" />
          <span className="ml-2 hidden sm:inline">Chia sẻ màn hình</span>
        </Button>

        <Button
          variant="livekit"
          onClick={onToggleChat}
          aria-label="Trò chuyện"
        >
          <MessageSquare className="size-5" />
          <span className="ml-2 hidden sm:inline">Trò chuyện</span>
        </Button>

       

        <Button
          variant="livekitDestructive"
          onClick={onLeave}
          aria-label="Thoát"
          className="ml-2"
        >
          <LogOut className="size-5" />
          <span className="ml-2 hidden sm:inline">Thoát</span>
        </Button>
      </div>
      <Button
          variant="livekit"
          onClick={onToggleControlPanel}
          aria-label="Điều khiển"
        >
          <Settings className="size-5" />
          <span className="ml-2 hidden sm:inline">Điều khiển</span>
        </Button>
    </div>
  );
}
